import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import { postAiJob } from "@/utils/aiJobs";
import {
  BarChart3, TrendingUp, Gauge, Sparkles, Target, ShieldCheck,
  ArrowRight, Loader2, Check, X, AlertTriangle, Brain, FileText,
  Clock, MapPin, Euro, Percent, Copy, CheckCircle2, Lightbulb,
  Archive, Swords, Plus, Search, Tag, Trash2, Edit, Wand2, BookOpen, Trophy,
  Building2, BookMarked, Users, Signal, ClipboardCheck, PhoneCall
} from "lucide-react";
import { toast } from "sonner";
import { ESTADO_LABELS, ESTADO_CLS, fmtEur, Field, Kpi } from "./shared";

const RESULTADO_CLS = {
  GANADA:       "bg-green-500 text-white",
  PERDIDA:      "bg-rose-500 text-white",
  DESESTIMADA:  "bg-gray-400 text-white",
  SIN_PRESENTAR:"bg-stone-400 text-white",
};
const PRIORIDAD_CLS = { P0: "bg-rose-100 text-rose-700", P1: "bg-amber-100 text-amber-700", P2: "bg-blue-100 text-blue-700" };

export default function PostmortemRoadmap() {
  const [data, setData] = useState(null);
  const [licitaciones, setLicitaciones] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/tender360/postmortems`); setData(r.data);
      if (selected) { const upd = (r.data.items || []).find(it => it.id === selected.id); if (upd) setSelected(upd); }
    } catch { toast.error("Error cargando postmortems"); }
  };
  useEffect(() => { load(); axios.get(`${API}/licitaciones`).then(r => setLicitaciones(r.data || [])); /* eslint-disable-next-line */ }, []);

  const generateAI = async () => {
    if (!selected) return;
    setGenerating(true);
    try { const res = await postAiJob(`${API}/tender360/postmortems/${selected.id}/generate-ai`); toast.success("Análisis Pando generado"); setSelected({ ...selected, ...res }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message || "Error"); }
    finally { setGenerating(false); }
  };

  const toggleAction = async (actionId, done) => {
    if (!selected) return;
    try {
      await axios.put(`${API}/tender360/postmortems/${selected.id}/actions/${actionId}`, { done, notes: "" });
      load();
    } catch { toast.error("Error"); }
  };

  const del = async () => {
    if (!selected || !window.confirm("¿Eliminar este postmortem?")) return;
    try { await axios.delete(`${API}/tender360/postmortems/${selected.id}`); toast.success("Eliminado"); setSelected(null); load(); }
    catch { toast.error("Error"); }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="postmortem-roadmap">
      <div className="grid grid-cols-5 gap-3">
        <Kpi label="Postmortems"      val={data.kpis.total}/>
        <Kpi label="Ganadas"          val={data.kpis.won}/>
        <Kpi label="Perdidas"         val={data.kpis.lost}/>
        <Kpi label="Win rate"         val={`${data.kpis.win_rate_pct}%`}/>
        <Kpi label="Acciones abiertas" val={data.kpis.open_action_items}/>
      </div>

      {/* Open actions summary */}
      {(data.open_actions || []).length > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 p-3">
          <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2 flex items-center gap-1"><AlertTriangle size={12}/> Backlog acciones pendientes · {data.open_actions.length}</div>
          <div className="space-y-1">
            {data.open_actions.slice(0, 8).map(a => (
              <div key={a.id} className="text-xs flex items-start gap-2 border-b border-gray-50 pb-1">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PRIORIDAD_CLS[a.prioridad] || "bg-gray-100"}`}>{a.prioridad}</span>
                <div className="flex-1">
                  <div className="font-semibold">{a.accion}</div>
                  <div className="text-[10px] text-gray-500">Owner: {a.owner} · de <strong>{a.licitacion}</strong></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={()=>setShowForm(true)}
                className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#006528]"
                data-testid="pm-add-btn">
          <Plus size={13}/> Nuevo postmortem
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
            {(data.items || []).map(p => (
              <button key={p.id} onClick={()=>setSelected(p)}
                      className={`w-full text-left p-3 hover:bg-gray-50 ${selected?.id===p.id ? "bg-gray-50" : ""}`}
                      data-testid={`pm-item-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-bold text-gray-900 truncate">{p.licitacion_title}</div>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${RESULTADO_CLS[p.resultado]}`}>{p.resultado}</span>
                </div>
                {p.es_ejemplo && (
                  <span className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200 mt-1" data-testid={`pm-ejemplo-badge-${p.id}`}>
                    EJEMPLO · no cuenta en KPIs
                  </span>
                )}
                <div className="text-[10px] text-gray-500 mt-0.5">{p.licitacion_municipio}</div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                  {p.score_final_vsm != null && <span>VSM: <strong>{p.score_final_vsm}</strong></span>}
                  {p.score_ganador != null && <span>Ganador: <strong>{p.score_ganador}</strong></span>}
                  {p.ganador && <span className="truncate max-w-[100px]">👑 {p.ganador}</span>}
                </div>
              </button>
            ))}
            {!data.items?.length && <div className="p-6 text-center text-gray-400 text-xs">Sin postmortems</div>}
          </div>
        </div>

        <div className="lg:col-span-3">
          {!selected
            ? <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-xs">Selecciona un postmortem para ver el detalle</div>
            : <PostmortemDetail pm={selected} onGenerate={generateAI} generating={generating} onToggle={toggleAction} onDelete={del}/>}
        </div>
      </div>

      {showForm && <PostmortemForm licitaciones={licitaciones} onClose={()=>{setShowForm(false);load();}}/>}
    </div>
  );
}

function PostmortemDetail({ pm, onGenerate, generating, onToggle, onDelete }) {
  const a = pm.ai_analysis;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="pm-detail">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
        <div className="flex-1">
          <div className="text-sm font-bold text-gray-900">{pm.licitacion_title}</div>
          <div className="text-xs text-gray-500 mt-0.5">{pm.licitacion_municipio}</div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${RESULTADO_CLS[pm.resultado]}`}>{pm.resultado}</span>
            {pm.ganador && <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded">Ganador: {pm.ganador}</span>}
            {pm.es_ejemplo && (
              <span className="text-[10px] font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded border border-purple-200" data-testid="pm-detail-ejemplo-badge">
                EJEMPLO · no cuenta en KPIs reales de win rate
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onGenerate} disabled={generating}
                  className="bg-[#007934] text-white text-xs font-semibold px-2 py-1.5 rounded flex items-center gap-1 hover:bg-[#006528] disabled:opacity-40"
                  data-testid="pm-generate-ai-btn">
            {generating ? <Loader2 className="animate-spin" size={12}/> : <Brain size={12}/>}
            {a ? "Regenerar Pando" : "Analizar con Pando"}
          </button>
          <button onClick={onDelete} className="text-xs text-rose-600 hover:bg-rose-50 p-1.5 rounded"><Trash2 size={12}/></button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div><span className="text-gray-500">Score VSM:</span> <strong>{pm.score_final_vsm ?? "—"}</strong></div>
        <div><span className="text-gray-500">Score ganador:</span> <strong>{pm.score_ganador ?? "—"}</strong></div>
        <div><span className="text-gray-500">Viabilidades previas:</span> <strong>{pm.viability_history?.length || 0}</strong></div>
        <div><span className="text-gray-500">Drafts generados:</span> <strong>{pm.num_drafts_generados || 0}</strong></div>
      </div>

      {pm.notas_directivas && <div className="text-xs text-gray-700 bg-gray-50 p-2 rounded"><strong>Notas:</strong> {pm.notas_directivas}</div>}

      {a && (
        <>
          {(a.gap_tecnico || a.gap_economico) && (
            <div className="grid grid-cols-2 gap-3">
              {a.gap_tecnico && <div className="bg-indigo-50 border border-indigo-100 p-2 rounded text-xs"><strong>Gap técnico:</strong> {a.gap_tecnico}</div>}
              {a.gap_economico && <div className="bg-amber-50 border border-amber-100 p-2 rounded text-xs"><strong>Gap económico:</strong> {a.gap_economico}</div>}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(a.aciertos_replicables || []).length > 0 && (
              <div className="bg-green-50 rounded-lg p-2 border border-green-100">
                <h4 className="text-[10px] font-bold text-green-700 uppercase tracking-wide mb-1">Aciertos replicables</h4>
                <ul className="text-[11px] text-green-800 space-y-0.5 list-disc pl-4">
                  {a.aciertos_replicables.map((x,i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
            )}
            {(a.errores_identificados || []).length > 0 && (
              <div className="bg-rose-50 rounded-lg p-2 border border-rose-100">
                <h4 className="text-[10px] font-bold text-rose-700 uppercase tracking-wide mb-1">Errores identificados</h4>
                <ul className="text-[11px] text-rose-800 space-y-0.5 list-disc pl-4">
                  {a.errores_identificados.map((x,i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
            )}
          </div>

          {(a.lecciones_aprendidas || []).length > 0 && (
            <div>
              <h4 className="text-[10px] font-bold text-gray-700 uppercase tracking-wide mb-1">Lecciones aprendidas</h4>
              <ul className="text-xs text-gray-700 space-y-0.5 list-disc pl-4">
                {a.lecciones_aprendidas.map((x,i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          )}

          {(a.patrones_observados || []).length > 0 && (
            <div>
              <h4 className="text-[10px] font-bold text-gray-700 uppercase tracking-wide mb-1">Patrones observados</h4>
              <ul className="text-xs text-gray-700 space-y-0.5 list-disc pl-4">
                {a.patrones_observados.map((x,i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          )}

          {a.proyecciones && (
            <div className="bg-gray-50 p-2 rounded border-l-4 border-[#007934]">
              <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">Proyecciones</div>
              <p className="text-xs text-gray-900">{a.proyecciones}</p>
            </div>
          )}

          {(pm.action_items_tracking || []).length > 0 && (
            <div>
              <h4 className="text-[10px] font-bold text-gray-700 uppercase tracking-wide mb-2">Roadmap de acciones ({pm.action_items_tracking.filter(x=>!x.done).length} abiertas)</h4>
              <div className="space-y-2">
                {pm.action_items_tracking.map(ai => (
                  <div key={ai.id} className={`text-xs border rounded-lg p-2 ${ai.done ? "bg-gray-50 border-gray-100 opacity-60" : "bg-white border-gray-200"}`}>
                    <div className="flex items-start gap-2">
                      <button onClick={()=>onToggle(ai.id, !ai.done)}
                              data-testid={`pm-action-toggle-${ai.id}`}
                              className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${ai.done ? "bg-[#007934] border-[#007934]" : "bg-white border-gray-300 hover:border-[#007934]"}`}>
                        {ai.done && <Check size={10} className="text-white"/>}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PRIORIDAD_CLS[ai.prioridad] || "bg-gray-100"}`}>{ai.prioridad}</span>
                          <div className={`font-semibold ${ai.done ? "line-through" : "text-gray-900"}`}>{ai.accion}</div>
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">Owner: <strong>{ai.owner}</strong> · KPI: {ai.kpi_objetivo}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PostmortemForm({ licitaciones, onClose }) {
  const [form, setForm] = useState({ licitacion_id: "", resultado: "PERDIDA", score_final_vsm: "", score_ganador: "", ganador: "", notas_directivas: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.licitacion_id) { toast.error("Selecciona una licitación"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/tender360/postmortems`, {
        ...form,
        score_final_vsm: form.score_final_vsm ? parseFloat(form.score_final_vsm) : null,
        score_ganador: form.score_ganador ? parseFloat(form.score_ganador) : null,
      });
      toast.success("Postmortem creado"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-xl w-full p-5" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">Nuevo postmortem</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={16}/></button>
        </div>
        <div className="space-y-3 text-xs">
          <Field label="Licitación *">
            <select value={form.licitacion_id} onChange={e=>setForm({...form,licitacion_id:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1 bg-white">
              <option value="">— Elegir —</option>
              {licitaciones.map(l => {
                const label = `${l.municipio || "?"} · ${(l.titulo || "").slice(0, 70)}`;
                return <option key={l.id} value={l.id}>{label}</option>;
              })}
            </select>
          </Field>
          <Field label="Resultado">
            <select value={form.resultado} onChange={e=>setForm({...form,resultado:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1 bg-white">
              <option>GANADA</option><option>PERDIDA</option><option>DESESTIMADA</option><option>SIN_PRESENTAR</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Score VSM"><input type="number" step="0.1" value={form.score_final_vsm} onChange={e=>setForm({...form,score_final_vsm:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
            <Field label="Score ganador"><input type="number" step="0.1" value={form.score_ganador} onChange={e=>setForm({...form,score_ganador:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          </div>
          <Field label="Ganador"><input value={form.ganador} onChange={e=>setForm({...form,ganador:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Notas directivas"><textarea value={form.notas_directivas} onChange={e=>setForm({...form,notas_directivas:e.target.value})} rows={4} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs border border-gray-200 px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded flex items-center gap-1">
            {saving && <Loader2 className="animate-spin" size={12}/>} Crear
          </button>
        </div>
      </div>
    </div>
  );
}

